import React from 'react';

import { Controller, useForm } from 'react-hook-form';

import { DEFAULT_ERROR, useCustomSnackBar } from 'hooks/useCustomSnackBar';
import useUserHook from 'hooks/useUserHook';

import { useMutationChangePassword } from 'api/mutations/changePassword.mutation';
import { useQueryPasswordPatternData } from 'api/queries/passwordPatternData.query';
import { useQueryServerTime } from 'api/queries/serverTime.query';

import { PasswordInput } from 'components/common/PasswordInput/PasswordInput';

import { encrypt } from 'components/App/AuthPublicPages/NewDesign/Login/Login.utils';
import { Button } from 'components/DesignSystem/Button';
import { Modal } from 'components/DesignSystem/Modal';
import classes from 'components/Settings/Components/UserProfile/components/ChangePassword/ChangePasswordModal/changePasswordModal.module.scss';

type Props = {
    onClose: () => void;
    userEmail: string;
    open: boolean;
};

interface IChangePassword {
    currentPassword: string;
    newPassword: string;
    password_repeat: string;
}
export const ChangePasswordModal = ({ open, onClose, userEmail }: Props) => {
    const { ability, email } = useUserHook();
    const { mutate } = useMutationChangePassword();
    const isAdmin = ability.can('all', 'Admin');
    const {
        formState: { errors },
        handleSubmit,
        watch,
        control,
    } = useForm<IChangePassword>();
    const { handleSnackBar } = useCustomSnackBar();
    const { data: patternData } = useQueryPasswordPatternData({ email });
    const { data: serverTime } = useQueryServerTime();

    const onSubmit = (data) => {
        const params = {
            currentPassword: data.currentPassword,
            newPassword: data.newPassword,
            userName: userEmail,
        };
        const time = serverTime?.time as number;
        const payload = time !== 0 ? { encrypt: encrypt(JSON.stringify(params), time), time } : params;
        mutate(payload, {
            onSuccess: () => {
                handleSnackBar('Successfully saved!', 'success');
                onClose();
            },
            onError: (error) => {
                DEFAULT_ERROR({ error, handleError: handleSnackBar });
            },
        });
    };

    return (
        <Modal
            open={open}
            onClose={onClose}
            title={'Change password'}
            subtitle={<>{patternData ? patternData.message : 'Choose a unique password'}</>}
            actions={
                <>
                    <Button variant={'secondary'} onClick={onClose}>
                        Cancel
                    </Button>
                    <Button variant={'primary'} id={'changePassword_saveBtn'} onClick={handleSubmit(onSubmit)}>
                        Save
                    </Button>
                </>
            }
        >
            <div className={classes.ChangePasswordContainer}>
                <form
                    onSubmit={(e) => {
                        e.stopPropagation();
                        return handleSubmit(onSubmit)(e);
                    }}
                >
                    <div className={classes.inputWrapperContainer}>
                        {!isAdmin && (
                            <div className={classes.inputWrapper}>
                                <Controller
                                    render={({ field }) => (
                                        <PasswordInput
                                            {...field}
                                            placeholder={'Type your current password*'}
                                            id={'changePassword_currentPassword'}
                                            error={errors.currentPassword}
                                        />
                                    )}
                                    name={`currentPassword`}
                                    control={control}
                                    rules={{
                                        required: 'You must specify your old password',
                                    }}
                                    defaultValue={''}
                                />
                            </div>
                        )}

                        <div className={classes.inputWrapper}>
                            <Controller
                                render={({ field }) => (
                                    <PasswordInput
                                        {...field}
                                        placeholder={'Type your new password*'}
                                        id={'changePassword_newPassword'}
                                        error={errors.newPassword}
                                    />
                                )}
                                name={`newPassword`}
                                control={control}
                                rules={{
                                    required: 'You must specify your new password',
                                    pattern: {
                                        value: new RegExp(patternData?.pattern),
                                        message: `${patternData?.message}. Please try again`,
                                    },
                                }}
                                defaultValue={''}
                            />
                        </div>

                        <div className={classes.inputWrapper}>
                            <Controller
                                render={({ field }) => (
                                    <PasswordInput
                                        {...field}
                                        placeholder={'Retype your new password*'}
                                        id={'changePassword_repeatPassword'}
                                        error={errors.password_repeat}
                                    />
                                )}
                                name={`password_repeat`}
                                control={control}
                                rules={{
                                    required: 'You must retype your new password',
                                    pattern: {
                                        value: new RegExp(patternData?.pattern),
                                        message: `${patternData?.message}. Please try again`,
                                    },
                                    validate: (value) => value === watch('newPassword') || 'The passwords do not match',
                                }}
                                defaultValue={''}
                            />
                        </div>
                    </div>

                    <div className={classes.errorContainer}>
                        {errors.currentPassword && <p>{errors.currentPassword.message} </p>}
                        {errors.newPassword && <p>{errors.newPassword.message}</p>}
                        {errors.password_repeat && <p>{errors.password_repeat.message}</p>}
                    </div>
                </form>
            </div>
        </Modal>
    );
};
